/**
 * WindRanger HMI control-panel demo: one screen at a time, stage dwell timers,
 * output battery, analog output scaling, import/export, and the moving grid backdrop.
 */
(() => {
  const DEFAULT_STAGES = [
    { windSpeedSP: 10, dwellToOnMin: 0, dwellToOnSec: 2, dwellToOffMin: 0, dwellToOffSec: 2 },
    { windSpeedSP: 15, dwellToOnMin: 0, dwellToOnSec: 2, dwellToOffMin: 0, dwellToOffSec: 2 },
    { windSpeedSP: 20, dwellToOnMin: 0, dwellToOnSec: 2, dwellToOffMin: 5, dwellToOffSec: 0 },
    { windSpeedSP: 25, dwellToOnMin: 0, dwellToOnSec: 2, dwellToOffMin: 60, dwellToOffSec: 0 },
  ];

  const BATTERY = [
    { segments: 8, value: '100%', label: 'Output' },
    { segments: 6, value: '75%', label: 'Output' },
    { segments: 4, value: '50%', label: 'Output' },
    { segments: 2, value: '25%', label: 'Output' },
    { segments: 0, value: 'OFF', label: '' },
  ];

  const pad = (value) => String(value).padStart(2, '0');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const chevron = (d) => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${d}"/></svg>`;

  class PaWrDemo extends HTMLElement {
    connectedCallback() {
      this.windSpeed = 0;
      this.stageCount = 4;
      this.view = 'stage-display';
      this.editStage = 1;
      this.clockOffset = 0;
      this.clockFormOpen = false;
      this.brightness = 1;
      this.manualOutputs = [];
      this.analog = {
        windSpeedMin: 0.0,
        windSpeedMax: 99.9,
        analogOutA: 20.0,
        analogOutB: 4.0,
        analogOutHand: 12.0,
        analogOutControl: 'OFF',
      };
      this.stages = DEFAULT_STAGES.map((stage) => ({ ...stage, isActive: false, onAt: null, offAt: null }));

      this.screen = this.querySelector('[data-wr-screen]');
      this.windInput = this.querySelector('[data-wr-wind]');
      this.stageSelect = this.querySelector('[data-wr-stage-count]');

      this.onWind = () => this.setWind(Number(this.windInput.value));
      this.onStageCount = () => {
        this.stageCount = Number(this.stageSelect.value);
        this.editStage = Math.min(this.editStage, this.stageCount);
        this.render();
      };
      this.onClickBound = (event) => this.onClick(event);
      this.onInputBound = (event) => this.onInput(event);
      this.onChangeBound = (event) => this.onChange(event);

      this.windInput?.addEventListener('input', this.onWind);
      this.stageSelect?.addEventListener('change', this.onStageCount);
      this.addEventListener('click', this.onClickBound);
      this.addEventListener('input', this.onInputBound);
      this.addEventListener('change', this.onChangeBound);

      this.clockTimer = window.setInterval(() => this.updateClock(), 1000);
      this.logicTimer = window.setInterval(() => this.tickStages(), 250);

      this.render();
      this.grid = new SquaresGrid(this.closest('.pa-wr-demo-section')?.querySelector('[data-wr-demo-canvas]'));
    }

    disconnectedCallback() {
      this.windInput?.removeEventListener('input', this.onWind);
      this.stageSelect?.removeEventListener('change', this.onStageCount);
      this.removeEventListener('click', this.onClickBound);
      this.removeEventListener('input', this.onInputBound);
      this.removeEventListener('change', this.onChangeBound);
      window.clearInterval(this.clockTimer);
      window.clearInterval(this.logicTimer);
      this.grid?.destroy();
    }

    activeStages() {
      return this.stages.slice(0, this.stageCount);
    }

    now() {
      return new Date(Date.now() + this.clockOffset);
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      const { dataset } = target;

      if (dataset.wrView) {
        this.view = dataset.wrView;
        this.clockFormOpen = false;
        if (this.view === 'stage-output-test') this.manualOutputs = this.activeStages().map((s) => s.isActive);
        this.render();
      } else if ('wrStageReset' in dataset) {
        this.stages.forEach((stage) => Object.assign(stage, { isActive: false, onAt: null, offAt: null }));
        this.render();
      } else if ('wrExport' in dataset) {
        this.exportConfig();
      } else if ('wrImport' in dataset) {
        this.importConfig();
      } else if (dataset.wrEditStage) {
        this.editStage = Number(dataset.wrEditStage);
        this.render();
      } else if (dataset.wrToggle) {
        const index = Number(dataset.wrToggle);
        this.manualOutputs[index] = !this.manualOutputs[index];
        this.render();
      } else if (dataset.wrMode) {
        this.analog.analogOutControl = dataset.wrMode;
        this.render();
      } else if ('wrClockSet' in dataset) {
        if (this.clockFormOpen) this.confirmClock();
        else this.openClockForm();
      } else if ('wrClockConfig' in dataset) {
        this.openClockForm();
      } else if ('wrClockConfirm' in dataset) {
        this.confirmClock();
      } else if (dataset.wrBright) {
        this.brightness = clamp(this.brightness + (dataset.wrBright === 'up' ? 0.1 : -0.1), 0.5, 1.2);
        this.screen.style.filter = this.brightness === 1 ? '' : `brightness(${this.brightness})`;
      }
    }

    onInput(event) {
      const field = event.target.closest('[data-wr-field]');
      if (field) {
        const key = field.dataset.wrField;
        let value = Number.parseInt(field.value, 10);
        if (Number.isNaN(value)) return;
        if (key === 'windSpeedSP') value = clamp(value, 0, 100);
        else if (key.endsWith('Min')) value = clamp(value, 0, 60);
        else value = clamp(value, 0, 59);
        this.stages[this.editStage - 1][key] = value;
        return;
      }

      const analog = event.target.closest('[data-wr-analog]');
      if (analog) {
        const value = Number(analog.value);
        const min = Number(analog.min);
        const max = Number(analog.max);
        if (!Number.isNaN(value) && value >= min && value <= max) {
          this.analog[analog.dataset.wrAnalog] = value;
          this.updateLive();
        }
      }
    }

    onChange(event) {
      const field = event.target.closest('[data-wr-field]');
      if (field) {
        const value = this.stages[this.editStage - 1][field.dataset.wrField];
        field.value = field.dataset.wrField === 'windSpeedSP' ? value : pad(value);
      }
      const analog = event.target.closest('[data-wr-analog]');
      if (analog) analog.value = this.analog[analog.dataset.wrAnalog].toFixed(1);
    }

    setWind(speed) {
      this.windSpeed = clamp(speed, 0, 99.9);
      this.grid?.setWind(this.windSpeed);
      this.updateLive();
    }

    /* ---------- stage logic ---------- */

    tickStages() {
      const now = Date.now();
      let changed = false;
      this.activeStages().forEach((stage) => {
        const above = this.windSpeed >= stage.windSpeedSP;
        if (above && !stage.isActive) {
          stage.offAt = null;
          stage.onAt ??= now + (stage.dwellToOnMin * 60 + stage.dwellToOnSec) * 1000;
          if (now >= stage.onAt) {
            stage.isActive = true;
            stage.onAt = null;
            changed = true;
          }
        } else if (!above && stage.isActive) {
          stage.onAt = null;
          stage.offAt ??= now + (stage.dwellToOffMin * 60 + stage.dwellToOffSec) * 1000;
          if (now >= stage.offAt) {
            stage.isActive = false;
            stage.offAt = null;
            changed = true;
          }
        } else {
          stage.onAt = null;
          stage.offAt = null;
        }
      });

      if (!changed) return;
      if (this.view === 'stage-output-test') this.manualOutputs = this.activeStages().map((s) => s.isActive);
      if (['stage-display', 'stage-set-points', 'stage-output-test'].includes(this.view)) this.render();
      else this.renderBattery();
    }

    analogOutput() {
      const { analogOutControl, analogOutHand, analogOutA, analogOutB, windSpeedMin, windSpeedMax } = this.analog;
      if (analogOutControl === 'HAND') return analogOutHand;
      if (analogOutControl === 'OFF') return 4.0;
      const range = windSpeedMax - windSpeedMin || 1;
      return Number((((this.windSpeed - windSpeedMin) * (analogOutB - analogOutA)) / range + analogOutA).toFixed(1));
    }

    /* ---------- rendering ---------- */

    render() {
      const views = {
        main: () => this.viewMain(),
        'stage-display': () => this.viewDisplay(),
        'stage-set-points': () => this.viewSetPoints(),
        'stage-output-test': () => this.viewOutputTest(),
        'analog-output-control': () => this.viewAnalog(),
        'real-time-clock-set': () => this.viewClock(),
        'display-brightness': () => this.viewBrightness(),
        'alarm-log': () => this.viewAlarmLog(),
        'user-password': () => this.viewPassword(),
      };
      this.screen.innerHTML = (views[this.view] || views.main)();
      this.updateClock();
      this.updateLive();
      this.renderBattery();
    }

    updateLive() {
      const wind = `${this.windSpeed.toFixed(1)} MPH`;
      this.querySelectorAll('[data-wr-wind-value], [data-wr-wind-readout]').forEach((el) => {
        el.textContent = wind;
      });
      const out = this.querySelector('[data-wr-analog-out]');
      if (out) out.textContent = `${this.analogOutput().toFixed(1)} mA`;
    }

    updateClock() {
      const date = this.now();
      const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
      const weekday = date.toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
      this.querySelectorAll('[data-wr-time]').forEach((el) => { el.textContent = time; });
      this.querySelectorAll('[data-wr-date]').forEach((el) => { el.textContent = day; });
      this.querySelectorAll('[data-wr-day]').forEach((el) => { el.textContent = weekday; });
    }

    renderBattery() {
      const highest = this.activeStages().reduce((max, stage, index) => (stage.isActive ? index : max), -1);
      const state = BATTERY[highest + 1];
      const label = this.querySelector('[data-wr-output]');
      const cells = this.querySelector('[data-wr-battery]');
      if (label) label.innerHTML = `<div>${state.value}</div>${state.label ? `<div>${state.label}</div>` : ''}`;
      if (cells) {
        cells.innerHTML = Array.from({ length: 8 }, (_, i) => `<div class="pa-wr-hmi__cell${i < state.segments ? ' is-on' : ''}"></div>`).join('');
      }
    }

    header(title, { tight = false, alert = false } = {}) {
      return `
        <div class="pa-wr-hmi__header${tight ? ' pa-wr-hmi__header--tight' : ''}">
          <div class="pa-wr-hmi__time"><div data-wr-time></div><div data-wr-date></div></div>
          <div class="pa-wr-hmi__title${alert ? ' pa-wr-hmi__title--alert' : ''}">${title}</div>
          <div class="pa-wr-hmi__day" data-wr-day></div>
        </div>`;
    }

    readout(modifier = '') {
      return `
        <div class="pa-wr-hmi__readout${modifier ? ` pa-wr-hmi__readout--${modifier}` : ''}">
          <div>CURRENT WIND SPEED</div>
          <div data-wr-wind-readout></div>
        </div>`;
    }

    menuButton() {
      return '<button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--lg" data-wr-view="main">MAIN MENU</button>';
    }

    viewMain() {
      const item = (view, label) =>
        `<button type="button" class="pa-wr-hmi__menu-btn" data-wr-view="${view}">${label}</button>`;
      return `
        ${this.header('MAIN MENU')}
        <div class="pa-wr-hmi__menu">
          <div class="pa-wr-hmi__menu-col">
            ${item('display-brightness', 'DISPLAY\nCONTRAST')}
            ${item('real-time-clock-set', 'REAL TIME\nCLOCK SET')}
            ${item('alarm-log', 'ALARM\nLOG')}
          </div>
          <div class="pa-wr-hmi__menu-col">
            ${item('user-password', 'USER\nPASSWORD')}
          </div>
          <div class="pa-wr-hmi__menu-col">
            ${item('stage-set-points', 'STAGE\nSET-POINTS')}
            ${item('stage-output-test', 'STAGE\nOUTPUT\nTEST')}
            ${item('analog-output-control', 'ANALOG\nOUTPUT\nCONTROL')}
          </div>
        </div>
        <div class="pa-wr-hmi__center">
          <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--lg pa-wr-hmi__key--bordered" data-wr-view="stage-display">RETURN</button>
        </div>`;
    }

    viewDisplay() {
      const stages = this.activeStages();
      const chips = stages
        .map((stage, i) => `<div class="pa-wr-hmi__chip ${stage.isActive ? 'pa-wr-hmi__is-on' : 'pa-wr-hmi__is-off'}">STAGE ${i + 1}<br>${stage.isActive ? 'ACTIVE' : 'INACTIVE'}</div>`)
        .join('');
      const cards = [0, 1, 2, 3]
        .map((i) => {
          const stage = stages[i];
          if (!stage) return '<div></div>';
          return `
            <div class="pa-wr-hmi__card">
              <div class="pa-wr-hmi__card-title">STAGE ${i + 1} SET POINTS</div>
              <div class="pa-wr-hmi__card-grid">
                <div class="pa-wr-hmi__card-label">WIND SPEED SP</div>
                <div class="pa-wr-hmi__card-value">${stage.windSpeedSP} MPH</div>
                <div class="pa-wr-hmi__card-label">DWELL TO ON</div>
                <div class="pa-wr-hmi__card-pair"><div>${pad(stage.dwellToOnMin)} MIN</div><div>${pad(stage.dwellToOnSec)} SEC</div></div>
                <div class="pa-wr-hmi__card-label">DWELL TO OFF</div>
                <div class="pa-wr-hmi__card-pair"><div>${pad(stage.dwellToOffMin)} MIN</div><div>${pad(stage.dwellToOffSec)} SEC</div></div>
              </div>
            </div>`;
        })
        .join('');
      return `
        ${this.header(`${stages.length} STAGE DISPLAY`, { tight: true })}
        ${this.readout('display')}
        <div class="pa-wr-hmi__chips">${chips}</div>
        <div class="pa-wr-hmi__cards">${cards}</div>
        <div class="pa-wr-hmi__between">
          <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--lg" data-wr-stage-reset>STAGE RESET</button>
          ${this.menuButton()}
        </div>`;
    }

    viewSetPoints() {
      const stages = this.activeStages();
      const stage = stages[this.editStage - 1];
      const input = (key, max) =>
        `<input type="number" min="0" max="${max}" value="${key === 'windSpeedSP' ? stage[key] : pad(stage[key])}" data-wr-field="${key}" aria-label="${key}">`;
      const nav = stages
        .map((_, i) => `<button type="button" class="pa-wr-hmi__key${i + 1 === this.editStage ? ' pa-wr-hmi__key--on' : ''}" data-wr-edit-stage="${i + 1}">STAGE ${i + 1}</button>`)
        .join('');
      return `
        ${this.header('STAGE SET POINTS')}
        ${this.readout()}
        <div class="pa-wr-hmi__status-row">
          <div class="pa-wr-hmi__status-pill ${stage.isActive ? 'pa-wr-hmi__is-on' : 'pa-wr-hmi__is-off'}">STAGE ${this.editStage} ${stage.isActive ? 'ACTIVE' : 'INACTIVE'}</div>
        </div>
        <div class="pa-wr-hmi__editor">
          <div class="pa-wr-hmi__editor-title">STAGE ${this.editStage} SET POINTS</div>
          <div class="pa-wr-hmi__editor-grid">
            <div class="pa-wr-hmi__editor-label">WIND SPEED SP</div>
            <div class="pa-wr-hmi__editor-value pa-wr-hmi__editor-value--wide">${input('windSpeedSP', 100)}<span>MPH</span></div>
            <div class="pa-wr-hmi__editor-label">DWELL TO ON</div>
            <div class="pa-wr-hmi__editor-value">${input('dwellToOnMin', 60)}<span>MIN</span></div>
            <div class="pa-wr-hmi__editor-value">${input('dwellToOnSec', 59)}<span>SEC</span></div>
            <div class="pa-wr-hmi__editor-label">DWELL TO OFF</div>
            <div class="pa-wr-hmi__editor-value">${input('dwellToOffMin', 60)}<span>MIN</span></div>
            <div class="pa-wr-hmi__editor-value">${input('dwellToOffSec', 59)}<span>SEC</span></div>
          </div>
        </div>
        <div class="pa-wr-hmi__stage-nav">
          <button type="button" class="pa-wr-hmi__key" data-wr-view="main">MAIN MENU</button>
          ${nav}
        </div>`;
    }

    viewOutputTest() {
      const tests = this.activeStages()
        .map((_, i) => {
          const on = Boolean(this.manualOutputs[i]);
          return `
            <div class="pa-wr-hmi__test">
              <div class="pa-wr-hmi__test-title">STAGE ${i + 1} OUTPUT</div>
              <div class="pa-wr-hmi__test-row">
                <button type="button" class="pa-wr-hmi__key${on ? ' pa-wr-hmi__key--red' : ''}" data-wr-toggle="${i}" aria-pressed="${on}">${on ? 'ON' : 'OFF'}</button>
                <div class="pa-wr-hmi__test-state ${on ? 'pa-wr-hmi__is-on' : 'pa-wr-hmi__is-off'}">STAGE ${i + 1} ${on ? 'ACTIVE' : 'INACTIVE'}</div>
              </div>
            </div>`;
        })
        .join('');
      return `
        ${this.header('STAGE OUTPUT TEST')}
        ${this.readout('test')}
        <div class="pa-wr-hmi__tests">${tests}</div>
        <div class="pa-wr-hmi__center">${this.menuButton()}</div>`;
    }

    viewAnalog() {
      const a = this.analog;
      const field = (key, label, min, max) => `
        <label class="pa-wr-hmi__analog-field">
          <span>${label}</span>
          <input type="number" step="0.1" min="${min}" max="${max}" value="${a[key].toFixed(1)}" data-wr-analog="${key}">
        </label>`;
      const modeClass = { HAND: 'pa-wr-hmi__key--yellow', OFF: 'pa-wr-hmi__key--red', AUTO: 'pa-wr-hmi__key--on' };
      const modes = ['HAND', 'OFF', 'AUTO']
        .map((mode) => {
          const on = a.analogOutControl === mode;
          return `<button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--mode${on ? ` ${modeClass[mode]}` : ''}" data-wr-mode="${mode}" aria-pressed="${on}">${mode}</button>`;
        })
        .join('');
      return `
        ${this.header('ANALOG OUTPUT')}
        ${this.readout('analog')}
        <div class="pa-wr-hmi__analog">
          <div class="pa-wr-hmi__box">
            <div class="pa-wr-hmi__box-title pa-wr-hmi__box-title--lg">ANALOG OUT SCALE AUTO MODE</div>
            <div class="pa-wr-hmi__analog-grid">
              <div class="pa-wr-hmi__analog-col">
                ${field('windSpeedMin', 'WIND SPEED MIN', 0.1, 99.9)}
                ${field('windSpeedMax', 'WIND SPEED MAX', 0.1, 99.9)}
              </div>
              <div class="pa-wr-hmi__analog-col">
                ${field('analogOutA', 'ANALOG OUT a', 4, 20)}
                ${field('analogOutB', 'ANALOG OUT b', 4, 20)}
              </div>
            </div>
          </div>
          <div class="pa-wr-hmi__analog-controls">
            <div class="pa-wr-hmi__box">
              <div class="pa-wr-hmi__box-title pa-wr-hmi__box-title--teal">ANALOG OUT HAND</div>
              <input class="pa-wr-hmi__hand-input" type="number" step="0.1" min="4" max="20" value="${a.analogOutHand.toFixed(1)}" data-wr-analog="analogOutHand" aria-label="Analog out hand">
            </div>
            <div class="pa-wr-hmi__box">
              <div class="pa-wr-hmi__box-title">ANALOG OUT CONTROL</div>
              <div class="pa-wr-hmi__modes">${modes}</div>
            </div>
          </div>
          <div class="pa-wr-hmi__box pa-wr-hmi__analog-out">
            <div class="pa-wr-hmi__box-title">CURRENT ANALOG OUT</div>
            <div data-wr-analog-out></div>
          </div>
        </div>
        <div class="pa-wr-hmi__center">${this.menuButton()}</div>`;
    }

    viewClock() {
      const form = this.clockFormOpen
        ? `
          <div class="pa-wr-hmi__clock-form">
            <div>
              <label for="wr-clock-date">Date:</label>
              <input id="wr-clock-date" type="date" value="${this.clockDraft.date}" data-wr-clock-date>
              <label for="wr-clock-time">Time:</label>
              <input id="wr-clock-time" type="time" step="1" value="${this.clockDraft.time}" data-wr-clock-time>
            </div>
            <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--confirm" data-wr-clock-confirm>CONFIRM</button>
          </div>`
        : '';
      return `
        ${this.header('REAL TIME CLOCK SET')}
        <div class="pa-wr-hmi__stack">
          <div class="pa-wr-hmi__clock-btns">
            <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--teal" data-wr-clock-set>SET DATE / TIME</button>
            <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--config" data-wr-clock-config>CONFIG.</button>
          </div>
          ${form}
          <p class="pa-wr-hmi__note">1. NOTE THAT THE TIME CLOCK USES 24 HOUR CONVENTION</p>
          <p class="pa-wr-hmi__note">2. FOR THIS WEB MOCKUP ITS JUST FOR SHOW</p>
        </div>
        <div class="pa-wr-hmi__center">${this.menuButton()}</div>`;
    }

    viewBrightness() {
      return `
        ${this.header('DISPLAY BRIGHTNESS')}
        <div class="pa-wr-hmi__stack">
          <div class="pa-wr-hmi__bright">
            <div>
              <p>BRIGHT UP</p>
              <button type="button" data-wr-bright="up" aria-label="Increase brightness">${chevron('m18 15-6-6-6 6')}</button>
            </div>
            <div>
              <p>BRIGHT DOWN</p>
              <button type="button" data-wr-bright="down" aria-label="Decrease brightness">${chevron('m6 9 6 6 6-6')}</button>
            </div>
          </div>
        </div>
        <div class="pa-wr-hmi__center">${this.menuButton()}</div>`;
    }

    viewAlarmLog() {
      const rows = Array.from({ length: 10 }, (_, i) => {
        const type = i % 2 === 0 ? 'Clear' : 'Warning';
        return `<tr class="is-${type.toLowerCase()}"><td>2023/08/15 09:30:18</td><td>Device</td><td>${(9 - i).toFixed(4)}</td><td>${type}</td><td>Alarm Description</td></tr>`;
      }).join('');
      return `
        ${this.header('ALARM LOG')}
        <div class="pa-wr-hmi__table">
          <table>
            <thead><tr><th>Alarm Time</th><th>Device</th><th>Value</th><th>Alarm Type</th><th>Description</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <div class="pa-wr-hmi__center">${this.menuButton()}</div>`;
    }

    viewPassword() {
      return `
        ${this.header('USER PASSWORD', { alert: true })}
        <div class="pa-wr-hmi__stack pa-wr-hmi__password">
          <div class="pa-wr-hmi__password-box">
            <div>USER PASSWORD</div>
            <div><p>12345123</p></div>
          </div>
          <div class="pa-wr-hmi__caution">
            <div>
              <strong>!!!CAUTION!!!</strong>
              <span>DO NOT FORGET THIS PASSWORD.</span>
              <span>IF THE PASSWORD IS FORGOTTEN THE<br>CONTROL UNIT WILL HAVE TO BE RETURNED<br>TO THE FACTORY FOR RESETTING</span>
            </div>
          </div>
          <button type="button" class="pa-wr-hmi__key pa-wr-hmi__key--lg pa-wr-hmi__key--raised" data-wr-view="main">RETURN</button>
        </div>`;
    }

    /* ---------- clock ---------- */

    openClockForm() {
      const date = this.now();
      const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
      this.clockDraft = { date: local.slice(0, 10), time: local.slice(11, 19) };
      this.clockFormOpen = true;
      this.render();
    }

    confirmClock() {
      const date = this.querySelector('[data-wr-clock-date]')?.value;
      const time = this.querySelector('[data-wr-clock-time]')?.value;
      if (date && time) {
        const next = new Date(`${date}T${time.length === 5 ? `${time}:00` : time}`);
        if (!Number.isNaN(next.getTime())) this.clockOffset = next.getTime() - Date.now();
      }
      this.clockFormOpen = false;
      this.render();
    }

    /* ---------- import / export ---------- */

    exportConfig() {
      const config = btoa(
        JSON.stringify({
          stageConfiguration: this.stageCount,
          stages: this.stages.map((stage) => ({
            windSpeedSP: stage.windSpeedSP,
            dwellToOn: `${stage.dwellToOnMin}:${stage.dwellToOnSec}`,
            dwellToOff: `${stage.dwellToOffMin}:${stage.dwellToOffSec}`,
          })),
          analogOutput: this.analog,
        }),
      );
      const fail = () => window.alert(`${this.dataset.exportFail} ${config}`);
      if (!navigator.clipboard?.writeText) {
        fail();
        return;
      }
      navigator.clipboard.writeText(config).then(() => window.alert(this.dataset.exportOk), fail);
    }

    importConfig() {
      const encoded = window.prompt(this.dataset.importPrompt);
      if (!encoded) return;
      try {
        const parsed = JSON.parse(atob(encoded.trim()));
        if (!Array.isArray(parsed.stages)) throw new Error('invalid');
        this.stageCount = clamp(Number(parsed.stageConfiguration) || 4, 1, 4);
        this.stageSelect.value = String(this.stageCount);
        this.editStage = Math.min(this.editStage, this.stageCount);
        parsed.stages.slice(0, 4).forEach((stage, index) => {
          const [onMin, onSec] = String(stage.dwellToOn).split(':').map(Number);
          const [offMin, offSec] = String(stage.dwellToOff).split(':').map(Number);
          Object.assign(this.stages[index], {
            windSpeedSP: clamp(Number(stage.windSpeedSP) || 0, 0, 100),
            dwellToOnMin: clamp(onMin || 0, 0, 60),
            dwellToOnSec: clamp(onSec || 0, 0, 59),
            dwellToOffMin: clamp(offMin || 0, 0, 60),
            dwellToOffSec: clamp(offSec || 0, 0, 59),
            isActive: false,
            onAt: null,
            offAt: null,
          });
        });
        if (parsed.analogOutput) {
          const next = { ...this.analog, ...parsed.analogOutput };
          ['windSpeedMin', 'windSpeedMax', 'analogOutA', 'analogOutB', 'analogOutHand'].forEach((key) => {
            next[key] = Number(next[key]) || 0;
          });
          if (!['HAND', 'OFF', 'AUTO'].includes(next.analogOutControl)) next.analogOutControl = 'OFF';
          this.analog = next;
        }
        this.render();
        window.alert(this.dataset.importOk);
      } catch {
        window.alert(this.dataset.importFail);
      }
    }
  }

  /** Scrolling square grid behind the demo; speed tracks the simulated wind. */
  class SquaresGrid {
    constructor(canvas) {
      if (!canvas) return;
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.size = 50;
      this.speed = 0.2;
      this.offset = 0;
      this.visible = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.resize = () => {
        this.canvas.width = this.canvas.offsetWidth;
        this.canvas.height = this.canvas.offsetHeight;
        this.draw();
      };
      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(canvas);
      this.intersection = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        this.toggle();
      });
      this.intersection.observe(canvas);
      this.resize();
    }

    setWind(windSpeed) {
      if (!this.canvas) return;
      this.speed = windSpeed <= 0 ? 0.2 : windSpeed >= 60 ? 10 : 0.2 + (windSpeed / 60) * 9.8;
    }

    toggle() {
      if (this.visible && !this.motion.matches) {
        if (!this.raf) this.raf = requestAnimationFrame(() => this.frame());
      } else if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    }

    frame() {
      this.offset = (this.offset - Math.max(this.speed, 0.1) + this.size) % this.size;
      this.draw();
      this.raf = requestAnimationFrame(() => this.frame());
    }

    draw() {
      const { ctx, size } = this;
      const { width, height } = this.canvas;
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = '#0dbf43';
      for (let x = -size; x < width + size; x += size) {
        for (let y = 0; y < height + size; y += size) {
          ctx.strokeRect(x - this.offset + size, y, size, size);
        }
      }
      const radius = Math.sqrt(width ** 2 + height ** 2) / 2;
      const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, radius);
      gradient.addColorStop(0, 'rgba(250, 250, 250, 0)');
      gradient.addColorStop(1, '#fafafa');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    destroy() {
      this.resizeObserver?.disconnect();
      this.intersection?.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
    }
  }

  if (!customElements.get('pa-wr-demo')) customElements.define('pa-wr-demo', PaWrDemo);
})();
